import { useAtomValue } from 'jotai';
import React from 'react';

import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Typography,
} from '@mui/material';

import { IFile } from '../models/IFile';
import { selectedStateAtom } from '../utils/AtomConfig';

interface FileInfoPanelProps {
  file: IFile | null;
  onClose: () => void;
}

export function FileInfoPanel({ file, onClose }: FileInfoPanelProps) {
  const selectedState = useAtomValue(selectedStateAtom);

  if (!file) {
    return null;
  }

  const details = [];
  if (selectedState === 'QLD') {
    details.push(
      { label: 'Location', value: file.qldLocation },
      { label: 'Department', value: file.qldDepartment },
      { label: 'Document Category', value: file.qldDocCategory },
      { label: 'Document Type', value: file.qldDocType },
      { label: 'Function', value: file.qldFunction }
    );
  } else if (selectedState === 'NSW') {
    details.push(
      { label: 'Location', value: file.nswLocation },
      { label: 'Department', value: file.nswDepartment },
      { label: 'Document Category', value: file.nswDocCategory },
      { label: 'Document Type', value: file.nswDocType }
    );
  }

  details.push(
    { label: 'File Type', value: file.fileType },
    {
      label: 'File Size',
      value:
        file.fileSize >= 1024 * 1024
          ? `${(file.fileSize / (1024 * 1024)).toFixed(2)} MB`
          : `${(file.fileSize / 1024).toFixed(2)} KB`,
    },
    { label: 'Author', value: file.author.replace(/;/g, ', ') },
    {
      label: 'Created Date',
      value: new Date(file.createdDate).toLocaleDateString(),
    },
    { label: 'Modified By', value: file.modifiedBy },
    {
      label: 'Modified Date',
      value: new Date(file.modifiedDate).toLocaleDateString(),
    }
  );

  return (
    <Drawer anchor='right' open={!!file} onClose={onClose}>
      <Box sx={{ width: 400, p: 2 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 2,
          }}
        >
          <Typography variant='h6' component='div'>
            File Details
          </Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
        <Typography variant='h5' component='div' sx={{ mb: 2 }}>
          {file.fileName}
        </Typography>
        <List dense>
          {details.map((detail) => (
            <ListItem key={detail.label} disablePadding>
              <ListItemText
                primary={detail.label}
                secondary={detail.value || '-'}
              />
            </ListItem>
          ))}
        </List>
      </Box>
    </Drawer>
  );
}
